/**
 * Énumérations métier du module CAC — Commissariat aux comptes.
 *
 * TYPE_RAPPORT_CAC / CATEGORIE_RESERVE : listes provisoires, les listes officielles n'ayant pas
 * été transmises par la DGPE.
 */

/**
 * Qualité du commissaire (code du référentiel `TYPE_COMMISSAIRE_AUX_COMPTES`).
 * Typé `string` : la liste est administrée côté DGPE, une union figée reproduirait le
 * défaut constaté sur `TYPE_AUDIT` (valeur ajoutée au référentiel → code brut à l'écran).
 * Libellés via `ReferentielService`, jamais en dur.
 */
export type TypeCac = string;

/** Nature du mandataire (code du référentiel `TYPE_MANDATAIRE_CAC`). */
export type TypeMandataire = string;

/** Statut calculé du mandat. */
export type StatutMandat = 'EN_COURS' | 'NON_RENOUVELE' | 'EXPIRE_NON_RENOUVELABLE';

/**
 * Opinion formulée par le CAC sur l'exercice : code du référentiel
 * `OPINION_FORMULEE_COMMISSARIAT_AUX_COMPTES`.
 */
export type OpinionCac = string;

/**
 * Code du référentiel `TYPE_ACTE_NOMINATION` (parameter_values) — source de vérité backend.
 *
 * Typé `string` : l'union figée qui existait ici déclarait des codes inventés
 * (`ARRETE_MINISTERIEL`, `ARRETE_INTERMINISTERIEL`, `PV_AGC`, `STATUTS`) absents du
 * référentiel, qui expose `DECRET`, `ARRETE`, `COURRIER_MPEEP`, `PV_AG`, `PV_CA`. Les
 * mandats portant `ARRETE` ou `COURRIER_MPEEP` affichaient « undefined ». Les libellés
 * se résolvent via `ReferentielService.getValues('TYPE_ACTE_NOMINATION')`, jamais en dur.
 */
export type TypeActeNomination = string;

/** Motif de modification d'un mandat. */
export type MotifModification = 'MODIFICATION_ACTE' | 'REMPLACEMENT' | 'REGULARISATION';

/**
 * Catégorie de société — code du référentiel `CATEGORIE_SOCIETE`, qui expose la nature
 * BANCAIRE / NON_BANCAIRE (constat runtime sur les 17 sociétés du portefeuille).
 *
 * L'union précédente (`SODE` | `SPFPM` | `CONTROLEE`) ne correspondait à aucune valeur
 * réelle : ces codes relèvent de `TYPE_ENTITE` (cf. `TYPES_ENTITE_CAC`), pas de la
 * catégorie. Les sociétés étaient donc castées vers un type faux.
 */
export type CategorieSociete = string;

/**
 * Types d'entité du Portefeuille de l'État éligibles à un rattachement CAC :
 * tous les `TYPE_ENTITE` sauf `AE` (Agence d'Exécution), soit société d'État
 * (SODE) et SPFP (majoritaire / minoritaire / minoritaire contrôlée).
 * Porté par le champ société `type_societe` (référentiel `TYPE_ENTITE`), et NON
 * `categorie_societe` (qui code la nature bancaire/non-bancaire).
 */
export const TYPES_ENTITE_CAC = ['SODE', 'SPFP_MAJORITAIRE', 'SPFP_MINORITAIRE', 'SPFP_MINORITAIRE_CONTROLE'];

/**
 * Liste provisoire des 13 types de rapports CAC : la liste officielle n'a pas été transmise.
 * Placeholder pour ne pas bloquer l'écran exercices.
 */
export type TypeRapportCac =
    | 'RAPPORT_GENERAL'
    | 'RAPPORT_SPECIAL_CONVENTIONS'
    | 'RAPPORT_COMPTES_CONSOLIDES'
    | 'RAPPORT_PROCEDURES_CONTROLE_INTERNE'
    | 'RAPPORT_GESTION'
    | 'RAPPORT_RSE'
    | 'RAPPORT_COMMISSARIAT_APPORTS'
    | 'RAPPORT_AUGMENTATION_CAPITAL'
    | 'RAPPORT_REDUCTION_CAPITAL'
    | 'RAPPORT_DISTRIBUTION_ACOMPTES'
    | 'RAPPORT_TRANSFORMATION'
    | 'RAPPORT_FUSION_SCISSION'
    | 'RAPPORT_AUTRE';

/** Qualité d'alerte mandat (R-CAC-02 — précision plan 30/60/90 j). */
export type QualiteAlerteMandat = '30J' | '60J' | '90J';

export const STATUT_MANDAT_LABELS: Record<StatutMandat, string> = {
    EN_COURS: 'En cours',
    NON_RENOUVELE: 'Non renouvelé',
    EXPIRE_NON_RENOUVELABLE: 'Expiré (non renouvelable)',
};

export const MOTIF_MODIFICATION_LABELS: Record<MotifModification, string> = {
    MODIFICATION_ACTE: 'Modification d’acte',
    REMPLACEMENT: 'Remplacement',
    REGULARISATION: 'Régularisation',
};

export const TYPE_RAPPORT_CAC_LABELS: Record<TypeRapportCac, string> = {
    RAPPORT_GENERAL: 'Rapport général',
    RAPPORT_SPECIAL_CONVENTIONS: 'Rapport spécial sur les conventions réglementées',
    RAPPORT_COMPTES_CONSOLIDES: 'Rapport sur les comptes consolidés',
    RAPPORT_PROCEDURES_CONTROLE_INTERNE: 'Rapport sur les procédures de contrôle interne',
    RAPPORT_GESTION: 'Rapport sur le rapport de gestion',
    RAPPORT_RSE: 'Rapport RSE / informations extra-financières',
    RAPPORT_COMMISSARIAT_APPORTS: 'Rapport de commissariat aux apports',
    RAPPORT_AUGMENTATION_CAPITAL: 'Rapport sur augmentation de capital',
    RAPPORT_REDUCTION_CAPITAL: 'Rapport sur réduction de capital',
    RAPPORT_DISTRIBUTION_ACOMPTES: 'Rapport sur distribution d’acomptes sur dividendes',
    RAPPORT_TRANSFORMATION: 'Rapport sur transformation de la société',
    RAPPORT_FUSION_SCISSION: 'Rapport sur fusion / scission',
    RAPPORT_AUTRE: 'Autre rapport',
};

export const QUALITE_ALERTE_MANDAT_LABELS: Record<QualiteAlerteMandat, string> = {
    '30J': 'Expire sous 30 jours',
    '60J': 'Expire sous 60 jours',
    '90J': 'Expire sous 90 jours',
};

/**
 * Nature de mandataire désignant la personne elle-même : le mandat n'est alors porté par aucun
 * cabinet, et le champ « Cabinet d'expertise comptable » n'a pas lieu d'être. Le référentiel
 * `TYPE_MANDATAIRE_CAC` n'a que deux valeurs, celle-ci et `CABINET_EXPERT_COMPTABLE`.
 *
 * Dupliqué dans `gouvernance/.../f360-cac.config.ts` — qui porte le même commentaire — pour ne pas
 * créer de dépendance runtime entre les deux modules. Tenir les deux alignés.
 */
export const CODE_MANDATAIRE_INTUITU_PERSONAE = 'INTUITU_PERSONAE';

/** Vrai quand la nature retenue est l'intuitu personae, d'après l'identifiant de référentiel choisi. */
export function estIntuituPersonae(mandataireId: string, typesMandataire: { id: string; code: string }[]): boolean {
    return typesMandataire.find((t) => t.id === mandataireId)?.code === CODE_MANDATAIRE_INTUITU_PERSONAE;
}
